import React, { useState, useEffect } from "react";
import { Link } from "react-router-dom";
import { useLang } from "@/context/LangContext";
import { api } from "@/lib/api";
import ModeToggle from "@/components/ModeToggle";
import StatusBadge, { StatusDot } from "@/components/StatusBadge";
import RailwayDivider from "@/components/RailwayDivider";
import SeasonalStrip from "@/components/SeasonalStrip";
import SagreStrip from "@/components/SagreStrip";
import SEO from "@/components/SEO";
import ImageCredit from "@/components/ImageCredit";
import { ArrowRight, Footprints, Bike, Clock } from "lucide-react";

const HERO_IMG = "/images/homepage-4.jpg";
const TUNNEL_IMG = "/images/tappa-sortino-siracusa.jpg";

const Home = () => {
  const { t, lang } = useLang();
  const [mode, setMode] = useState("bike");
  const [tappe, setTappe] = useState([]);
  const [stato, setStato] = useState([]);

  useEffect(() => {
    api.get("/tappe").then((r) => setTappe(r.data.filter((t) => t.type !== "pedestrian"))).catch(() => {});
    api.get("/stato-cammino").then((r) => setStato(r.data.filter((s) => s.section !== "extra"))).catch(() => {});
  }, []);

  const sectionLabel = (s) => (s === "catania-siracusa" ? t("section_catania_siracusa") : t("section_siracusa_ragusa"));

  return (
    <div data-testid="home-page">
      <SEO
        titleIt="Cammino Ibleo UNESCO — 285 km da Catania a Ragusa Ibla"
        titleEn="Cammino Ibleo UNESCO — 285 km from Catania to Ragusa Ibla"
        descriptionIt="Il cammino ciclo-pedonale di 285 km sulle ex-ferrovie della Sicilia Orientale: 10 siti UNESCO, tappe, mappa interattiva, prodotti del territorio e sagre iblee."
        descriptionEn="A 285 km cycling and walking trail on the disused Eastern Sicily railways: 10 UNESCO sites, stages, interactive map, local products and Iblean food festivals."
        image={HERO_IMG}
      />
      <section className="relative overflow-hidden bg-grafite">
        {/* Desktop: split layout (texto 55% | foto portrait 45%). Mobile: stacked. */}
        <div className="grid lg:grid-cols-[1.2fr_1fr] min-h-[560px] lg:min-h-[760px]">
          {/* Lado texto */}
          <div className="relative flex flex-col justify-center bg-grafite px-6 lg:px-16 py-20 lg:py-24 order-2 lg:order-1">
            <div className="absolute inset-0 pointer-events-none opacity-[0.07]"
                 style={{ backgroundImage: "radial-gradient(circle at 20% 20%, #D9A24E 0%, transparent 55%)" }} />
            <div className="relative z-10 max-w-2xl">
              <div className="eyebrow text-miele mb-6">{t("hero_eyebrow")}</div>
              <h1 className="font-serif text-white text-5xl sm:text-6xl lg:text-7xl xl:text-8xl leading-[0.95] tracking-tight mb-6">
                {t("hero_title_1")}
                <br />
                <span className="italic text-miele">{t("hero_title_2")}</span>
                <span className="block text-2xl sm:text-3xl lg:text-4xl mt-4 text-pietra-light/85 not-italic">
                  {t("hero_title_3")}
                </span>
              </h1>
              <p className="text-base lg:text-lg text-pietra-light/85 max-w-xl mb-10 leading-relaxed">{t("hero_subtitle")}</p>

              <div className="flex flex-wrap items-center gap-4">
                <Link to="/tappe" data-testid="hero-cta-walk" className="inline-flex items-center gap-3 bg-pietra-light text-grafite px-7 py-3.5 rounded-sm font-semibold hover:bg-miele transition-colors">
                  <Footprints size={18} /> {t("hero_cta_walk")}
                </Link>
                <Link to="/tappe" data-testid="hero-cta-bike" className="inline-flex items-center gap-3 border-2 border-pietra-light text-pietra-light px-7 py-3.5 rounded-sm font-semibold hover:bg-pietra-light hover:text-grafite transition-colors">
                  <Bike size={18} /> {t("hero_cta_bike")}
                </Link>
              </div>
            </div>
          </div>

          {/* Lado foto — respeta aspect ratio nativo portrait, sin zoom */}
          <div className="relative order-1 lg:order-2 min-h-[380px] sm:min-h-[460px] lg:min-h-full bg-grafite">
            <img
              loading="eager"
              decoding="async"
              fetchPriority="high"
              src={HERO_IMG}
              alt=""
              className="w-full h-full object-cover"
              style={{ objectPosition: "50% 50%" }}
            />
            <div className="absolute inset-0 bg-gradient-to-r from-grafite/40 via-transparent to-transparent lg:bg-gradient-to-r lg:from-grafite/60 lg:via-transparent lg:to-transparent" />
            <ImageCredit src={HERO_IMG} size="lg" linkVeca />
          </div>
        </div>

        {/* Barra stats debajo del hero */}
        <div className="relative z-10 bg-carrubo/95 backdrop-blur-sm border-t border-miele/30">
          <div className="max-w-[1400px] mx-auto px-6 lg:px-12 py-7 grid grid-cols-2 md:grid-cols-4 gap-x-10 md:gap-x-14 gap-y-6 text-pietra-light">
            {[
              { n: "285", label: t("stat_km") },
              { n: "10", label: t("stat_tappe") },
              { n: "9", label: t("stat_unesco") },
              { n: "1", label: t("stat_ferrovia") },
            ].map((s, i) => (
              <div
                key={i}
                data-testid={`hero-stat-${i}`}
                className={`flex items-baseline gap-3 min-w-0 ${i !== 0 ? "md:pl-10 md:border-l md:border-pietra-light/15" : ""}`}
              >
                <div className="font-serif text-4xl md:text-5xl lg:text-6xl text-miele leading-none flex-shrink-0">{s.n}</div>
                <div className="text-[0.65rem] md:text-[0.7rem] uppercase tracking-[0.18em] font-semibold opacity-85 leading-tight break-words">{s.label}</div>
              </div>
            ))}
          </div>
        </div>
      </section>

      <section data-testid="section-ferrovia" className="py-24 md:py-32 bg-pietra-light">
        <div className="max-w-[1400px] mx-auto px-6 lg:px-12 grid md:grid-cols-2 gap-16 items-center">
          <div>
            <div className="eyebrow mb-6">{t("ferrovia_eyebrow")}</div>
            <h2 className="font-serif text-4xl md:text-6xl leading-[1.05] text-grafite mb-8">{t("ferrovia_title")}</h2>
            <RailwayDivider className="mb-8" />
            <p className="text-lg text-grafite-soft leading-relaxed mb-6">{t("ferrovia_body")}</p>
            <p className="font-serif text-2xl text-carrubo italic">{t("ferrovia_stations_label")}</p>
          </div>
          <div className="relative">
            <img loading="lazy" decoding="async" src={TUNNEL_IMG} alt="" className="w-full h-[400px] md:h-[600px] object-cover rounded-sm" />
            <ImageCredit src={TUNNEL_IMG} size="lg" linkVeca />
            <div className="absolute bottom-6 left-6 right-6 bg-pietra-light/95 backdrop-blur-sm p-6 rounded-sm">
              <div className="small-caps text-miele mb-1">1868 → 1956 → 2026</div>
              <div className="font-serif text-2xl text-grafite">Catania · Siracusa · Ragusa</div>
            </div>
          </div>
        </div>
      </section>

      <section data-testid="section-modes" className="py-24 md:py-32 bg-pietra">
        <div className="max-w-[1400px] mx-auto px-6 lg:px-12">
          <div className="text-center mb-16">
            <div className="eyebrow justify-center flex mb-4">{t("modes_eyebrow")}</div>
            <h2 className="font-serif text-4xl md:text-6xl text-grafite mb-6">{t("modes_title")}</h2>
            <div className="flex justify-center"><ModeToggle mode={mode} setMode={setMode} /></div>
          </div>

          <div className="grid md:grid-cols-2 gap-8">
            <div className={`p-10 rounded-sm border-2 transition-all ${mode === "walk" ? "bg-carrubo text-pietra-light border-carrubo" : "bg-pietra-light border-carrubo/10 text-grafite"}`}>
              <Footprints size={40} strokeWidth={1.3} className="mb-6" />
              <h3 className="font-serif text-3xl mb-4">{t("walk_title")}</h3>
              <p className="text-base leading-relaxed mb-6 opacity-90">{t("walk_desc")}</p>
              <div className="flex items-center gap-3 pt-6 border-t border-current/20">
                <Clock size={16} />
                <span className="small-caps">{t("walk_time")}: {t("walk_time_val")}</span>
              </div>
            </div>
            <div className={`p-10 rounded-sm border-2 transition-all ${mode === "bike" ? "bg-carrubo text-pietra-light border-carrubo" : "bg-pietra-light border-carrubo/10 text-grafite"}`}>
              <Bike size={40} strokeWidth={1.3} className="mb-6" />
              <h3 className="font-serif text-3xl mb-4">{t("bike_title")}</h3>
              <p className="text-base leading-relaxed mb-6 opacity-90">{t("bike_desc")}</p>
              <div className="flex items-center gap-3 pt-6 border-t border-current/20">
                <Clock size={16} />
                <span className="small-caps">{t("bike_time")}: {t("bike_time_val")}</span>
              </div>
            </div>
          </div>
        </div>
      </section>

      <section data-testid="section-stato" className="py-24 md:py-32 bg-pietra-light">
        <div className="max-w-[1200px] mx-auto px-6 lg:px-12">
          <div className="mb-12">
            <div className="eyebrow mb-4">{t("stato_eyebrow")}</div>
            <h2 className="font-serif text-4xl md:text-5xl text-grafite mb-4">{t("stato_title")}</h2>
            <p className="text-lg text-grafite-soft max-w-3xl leading-relaxed">{t("stato_intro")}</p>
          </div>

          {["catania-siracusa", "siracusa-ragusa"].map((sec) => (
            <div key={sec} className="mb-8">
              <div className="small-caps text-miele mb-4 flex items-center gap-3">
                <span>{sectionLabel(sec)}</span>
                <span className="flex-1 railway-line opacity-40" />
              </div>
              <div className="bg-pietra border border-carrubo/15 rounded-sm">
                {stato.filter((s) => s.section === sec).map((s, i, arr) => (
                  <Link key={s.slug} to={`/tappe/${s.slug}`} data-testid={`stato-row-${s.slug}`}
                    className={`flex items-center justify-between p-5 md:p-6 hover:bg-pietra-light transition-colors ${i !== arr.length - 1 ? "border-b border-carrubo/10" : ""}`}>
                    <div className="flex items-center gap-5">
                      <div className="font-serif text-2xl text-miele w-8">{String(s.order).padStart(2, "0")}</div>
                      <div>
                        <div className="font-semibold text-grafite">{lang === "it" ? s.title_it : s.title_en}</div>
                        <div className="text-xs text-grafite-soft mt-1">{s.km} km</div>
                      </div>
                    </div>
                    <StatusBadge status={s.status} />
                  </Link>
                ))}
              </div>
            </div>
          ))}

          <div className="flex flex-wrap items-center gap-6 mt-8 text-sm">
            <span className="flex items-center gap-2"><StatusDot status="open" /> {t("status_open")}</span>
            <span className="flex items-center gap-2"><StatusDot status="warn" /> {t("status_warn")}</span>
            <span className="flex items-center gap-2"><StatusDot status="closed" /> {t("status_closed")}</span>
          </div>
        </div>
      </section>

      <SeasonalStrip />
      <SagreStrip />

      <section className="py-24 md:py-32 bg-grafite text-pietra-light">
        <div className="max-w-[1400px] mx-auto px-6 lg:px-12">
          <div className="flex items-end justify-between mb-16 flex-wrap gap-6">
            <div>
              <div className="eyebrow mb-4">{t("nav_tappe")}</div>
              <h2 className="font-serif text-4xl md:text-6xl">Dieci tappe, un solo cammino</h2>
            </div>
            <Link to="/tappe" className="inline-flex items-center gap-2 text-miele hover:text-pietra-light group">
              <span className="small-caps">Vedi tutte</span>
              <ArrowRight size={16} className="group-hover:translate-x-1 transition-transform" />
            </Link>
          </div>

          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6">
            {tappe.slice(0, 6).map((tp) => (
              <Link key={tp.slug} to={`/tappe/${tp.slug}`} data-testid={`tappa-card-${tp.slug}`}
                className="group relative overflow-hidden rounded-sm bg-grafite/60 border border-pietra-light/10 hover:border-miele/50 transition-all">
                <div className="aspect-[4/3] overflow-hidden">
                  <img loading="lazy" decoding="async" src={tp.image} alt="" className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-700" />
                </div>
                <div className="p-6">
                  <div className="flex items-center justify-between mb-3">
                    <span className="font-serif text-3xl text-miele">{String(tp.order).padStart(2, "0")}</span>
                    <StatusBadge status={tp.status} />
                  </div>
                  <h3 className="font-serif text-2xl mb-2">{tp[lang].title}</h3>
                  <p className="text-sm text-pietra-light/70 mb-4">{tp[lang].subtitle}</p>
                  <div className="flex gap-4 text-xs text-pietra-light/60 pt-4 border-t border-pietra-light/10">
                    <span>{tp.km} km</span>
                    <span>↑ {tp.elevation_gain_m} m</span>
                    <span>{mode === "walk" ? tp.walk_hours : tp.bike_hours}</span>
                  </div>
                </div>
              </Link>
            ))}
          </div>
        </div>
      </section>

      <section data-testid="section-partner-cta" className="py-24 md:py-32 bg-miele">
        <div className="max-w-[1200px] mx-auto px-6 lg:px-12 grid md:grid-cols-5 gap-12 items-center">
          <div className="md:col-span-3">
            <div className="eyebrow mb-4" style={{ color: "#3E5F44" }}><span style={{ color: "#3E5F44" }}>{t("partner_eyebrow")}</span></div>
            <h2 className="font-serif text-4xl md:text-6xl text-grafite mb-6">{t("partner_title")}</h2>
            <p className="text-lg text-grafite/80 leading-relaxed max-w-2xl">{t("partner_body")}</p>
          </div>
          <div className="md:col-span-2 flex md:justify-end">
            <Link to="/diventa-partner" className="inline-flex items-center gap-3 bg-carrubo hover:bg-carrubo-dark text-pietra-light px-8 py-5 rounded-sm font-semibold group">
              {t("partner_cta")}
              <ArrowRight size={18} className="group-hover:translate-x-1 transition-transform" />
            </Link>
          </div>
        </div>
      </section>
    </div>
  );
};

export default Home;
